import {ChangeAddressModal} from '@/components/sites/ChangeAddressModal';
import {Link, useParams} from 'react-router-dom';
import {Spinner} from '@/components/ui/Spinner';
import {buildAddressHost, formatAddressDate} from '@/lib/publication-address';
import {getSitePublicUrl} from '@/lib/site-url';
import {useSite} from '@/hooks/useSite';
import {useState} from 'react';
import type {SlugRenameResult} from '@/types/api';

const panelStyle: React.CSSProperties = {
    width: 'min(100%, 720px)',
    padding: 28,
    border: '1px solid var(--tt-card-border)',
    borderRadius: 14,
    background: 'var(--tt-card-bg)'
};

const rowLabelStyle: React.CSSProperties = {
    display: 'block',
    margin: 0,
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--tt-text-primary)'
};

const noteStyle: React.CSSProperties = {
    fontSize: '12px',
    lineHeight: 1.7,
    color: 'var(--tt-text-secondary)'
};

/** Names, in plain words, whatever the rename has not finished yet. */
function outstandingWork(result: SlugRenameResult): string[] {
    const items: string[] = [];
    if (result.url_rewrite_state !== 'done') {
        items.push('links saved inside your publication');
    }
    if (result.cache_invalidation_state !== 'done') {
        items.push('pages that were already cached');
    }
    return items;
}

function RenameResultPanel({result, siteUrl}: {result: SlugRenameResult; siteUrl: string}) {
    const outstanding = outstandingWork(result);
    const needsAttention = result.reconciliation_needs_attention;
    const forwardingUntil = formatAddressDate(result.forwarding_until);

    return (
        <div role="status"
            style={{...panelStyle, marginBottom: 24, borderColor: needsAttention ? 'var(--tt-red)' : 'var(--tt-card-border)'}}
        >
            <p style={{...rowLabelStyle, marginBottom: 10}}>Your address is now {buildAddressHost(siteUrl, result.slug)}</p>
            <ul style={{margin: '0 0 4px', paddingLeft: 18, display: 'grid', gap: 7}}>
                <li style={noteStyle}>
                    Links to <strong>{buildAddressHost(siteUrl, result.previous_slug)}</strong> keep working
                    {forwardingUntil ? ` until ${forwardingUntil}` : ' for 90 days'}, then stop.
                </li>
                <li style={noteStyle}>Anyone who was signed in on the old address has to sign in again.</li>
                <li style={noteStyle}>Your old address is held for you, so nobody else can take it.</li>
                <li style={noteStyle}>You can change the address again in 30 days.</li>
            </ul>

            {outstanding.length > 0 && (
                <p style={{...noteStyle, margin: '16px 0 0', color: needsAttention ? 'var(--tt-red)' : 'var(--tt-text-secondary)'}}>
                    {needsAttention
                        ? `Needs attention — we are still updating ${outstanding.join(' and ')}, and it is taking longer than expected. We keep retrying; everything still works through the old address in the meantime.`
                        : `Finishing up — we are still updating ${outstanding.join(' and ')}. A few details may take a few minutes.`}
                </p>
            )}

            {result.email_provider_webhook_update_required && (
                <p style={{...noteStyle, margin: '16px 0 0', padding: 14, borderRadius: 10, background: 'rgba(245, 11, 35, 0.06)', color: 'var(--tt-text-primary)'}}>
                    <strong>Check your email provider.</strong> You have an email tool connected to this publication.
                    If you gave it the old address anywhere in its own settings, update it there
                    {forwardingUntil ? ` before ${forwardingUntil}` : ' within 90 days'} — after that, messages it sends back to the old
                    address are dropped.
                </p>
            )}
        </div>
    );
}

export function PublicationSettingsPage() {
    const {id = ''} = useParams();
    const {data: site, isLoading, isError} = useSite(id);
    const [showChangeAddress, setShowChangeAddress] = useState(false);
    const [renameResult, setRenameResult] = useState<SlugRenameResult | null>(null);

    if (isLoading) {
        return <div className="studio-content-page"><Spinner /></div>;
    }

    if (isError || !site) {
        return (
            <div className="studio-content-page">
                <header className="studio-detail-heading">
                    <Link className="studio-back-link" to="/dashboard">← Publications</Link>
                    <h1 className="studio-page-title">Publication settings</h1>
                    <p className="studio-page-description">We could not load this publication. Please try again.</p>
                </header>
            </div>
        );
    }

    const currentSlug = renameResult?.slug ?? site.slug;
    const publicUrl = getSitePublicUrl(site);
    const addressHost = buildAddressHost(site.url, currentSlug);
    const isOwner = site.access_type === 'owner';
    const deletionPending = !!site.deletion_pending;
    const canChange = isOwner && site.status === 'active' && !deletionPending;

    let blockedReason = '';
    if (!isOwner) {
        blockedReason = 'Only the owner of this publication can change its address.';
    } else if (deletionPending) {
        blockedReason = 'This publication is being deleted, so its address cannot be changed.';
    } else if (site.status !== 'active') {
        blockedReason = 'You can change the address once this publication is live.';
    }

    return (
        <div className="studio-content-page">
            <header className="studio-detail-heading">
                <Link className="studio-back-link" to="/dashboard">← Publications</Link>
                <h1 className="studio-page-title">Publication settings</h1>
                <p className="studio-page-description">{site.name}</p>
            </header>

            {renameResult && <RenameResultPanel result={renameResult} siteUrl={site.url} />}

            <section style={panelStyle}>
                <h2 style={{...rowLabelStyle, fontSize: '15px', margin: '0 0 6px'}}>Publication address</h2>
                <p style={{...noteStyle, margin: '0 0 18px'}}>
                    This is where readers find your publication. You can change it once every 30 days.
                </p>

                <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px 18px', borderRadius: 10, background: 'var(--tt-input-bg)', border: '1px solid var(--tt-card-border)'}}>
                    <div style={{minWidth: 0}}>
                        <span style={{display: 'block', fontSize: '11px', color: 'var(--tt-text-secondary)', marginBottom: 4}}>Current address</span>
                        <a href={publicUrl} rel="noopener noreferrer" style={{fontSize: '14px', fontWeight: 500, color: 'var(--tt-text-primary)', wordBreak: 'break-all'}} target="_blank">
                            {addressHost}
                        </a>
                    </div>
                    <button className="studio-control" disabled={!canChange}
                        style={{height: 40, padding: '0 18px', borderRadius: 8, background: 'var(--tt-card-bg)', border: '1px solid var(--tt-card-border)', color: 'var(--tt-text-primary)', fontSize: '13px', fontWeight: 500, cursor: canChange ? 'pointer' : 'not-allowed', fontFamily: 'inherit'}}
                        type="button"
                        onClick={() => setShowChangeAddress(true)}
                    >
                        Change address
                    </button>
                </div>

                {blockedReason && <p style={{...noteStyle, margin: '14px 0 0'}}>{blockedReason}</p>}

                {site.custom_domain && (
                    <p style={{...noteStyle, margin: '14px 0 0'}}>
                        Your own domain <strong style={{color: 'var(--tt-text-primary)'}}>{site.custom_domain}</strong> keeps working exactly as it does now. Changing the
                        address above does not affect it.
                    </p>
                )}
            </section>

            {showChangeAddress && canChange && (
                <ChangeAddressModal
                    currentSlug={currentSlug}
                    siteId={site.id}
                    siteName={site.name}
                    siteUrl={site.url}
                    onCancel={() => setShowChangeAddress(false)}
                    onRenamed={(result) => {
                        setRenameResult(result);
                        setShowChangeAddress(false);
                    }}
                />
            )}
        </div>
    );
}
